import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface StreamingTextProps {
  content: string;
  isStreaming: boolean;
  className?: string;
  enableMarkdown?: boolean;
  chunkSize?: number; // number of characters per frame (approx.)
  baseDelayMs?: number; // base delay between frames
}

export function StreamingText({ content, isStreaming, className = '', enableMarkdown = false, chunkSize = 6, baseDelayMs = 18 }: StreamingTextProps) {
  const [displayedContent, setDisplayedContent] = useState('');
  const [lastDisplayedLength, setLastDisplayedLength] = useState(0);
  const contentRef = useRef<HTMLDivElement>(null);
  const animationFrameRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Only animate when content grows
    if (content.length > lastDisplayedLength) {
      const newChunk = content.slice(lastDisplayedLength);

      if (animationFrameRef.current) {
        clearTimeout(animationFrameRef.current);
        animationFrameRef.current = null;
      }

      let index = 0;
      const animateChunk = () => {
        if (index < newChunk.length) {
          const dynamicSize = Math.max(1, Math.round(chunkSize + (Math.random() * 2 - 1)));
          const nextSlice = newChunk.slice(index, index + dynamicSize);
          setDisplayedContent(prev => prev + nextSlice);
          index += nextSlice.length;

          const char = nextSlice[nextSlice.length - 1];
          let delay = baseDelayMs;
          if (char === ' ') delay = Math.max(6, baseDelayMs * 0.5);
          else if (char === '.' || char === '!' || char === '?') delay = baseDelayMs * 4.5;
          else if (char === ',' || char === ';' || char === ':') delay = baseDelayMs * 2.5;
          else if (char === '\n') delay = baseDelayMs * 3.0;

          animationFrameRef.current = setTimeout(animateChunk, delay);
        } else {
          setLastDisplayedLength(content.length);
        }
      };

      animateChunk();
    } else if (content.length < lastDisplayedLength) {
      // Reset (new stream started)
      setDisplayedContent(content);
      setLastDisplayedLength(content.length);
    } else if (!isStreaming && content !== displayedContent) {
      // Sync when streaming stops
      setDisplayedContent(content);
      setLastDisplayedLength(content.length);
    }

    return () => {
      if (animationFrameRef.current) {
        clearTimeout(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
    // Note: deliberately exclude displayedContent to avoid infinite update loops
  }, [content, isStreaming, chunkSize, baseDelayMs, lastDisplayedLength]);

const markdownComponents = {
  // Custom styling for markdown elements
  p: ({ children }: any) => <p className="text-white leading-relaxed mb-4 last:mb-0">{children}</p>,
  h1: ({ children }: any) => <h1 className="text-white text-2xl font-bold mb-4">{children}</h1>,
  h2: ({ children }: any) => <h2 className="text-white text-xl font-semibold mb-3">{children}</h2>,
  h3: ({ children }: any) => <h3 className="text-white text-lg font-medium mb-2">{children}</h3>,
  ul: ({ children }: any) => <ul className="text-white list-disc list-inside mb-4 space-y-1">{children}</ul>,
  ol: ({ children }: any) => <ol className="text-white list-decimal list-inside mb-4 space-y-1">{children}</ol>,
  li: ({ children }: any) => <li className="text-white">{children}</li>,
  blockquote: ({ children }: any) => <blockquote className="border-l-4 border-slate-600 pl-4 italic text-slate-300 mb-4">{children}</blockquote>,
  code: ({ children, className }: any) => {
    const isInline = !className;
    return isInline ? (
      <code className="bg-slate-800 text-slate-200 px-1.5 py-0.5 rounded text-sm font-mono">{children}</code>
    ) : (
      <code className="block bg-slate-800 text-slate-200 p-4 rounded-lg text-sm font-mono overflow-x-auto">{children}</code>
    );
  },
  pre: ({ children }: any) => <pre className="bg-slate-800 rounded-lg mb-4 overflow-hidden">{children}</pre>,
  a: ({ children, href }: any) => <a href={href} className="text-blue-400 hover:text-blue-300 underline" target="_blank" rel="noopener noreferrer">{children}</a>,
  strong: ({ children }: any) => <strong className="text-white font-semibold">{children}</strong>,
  em: ({ children }: any) => <em className="text-slate-200 italic">{children}</em>,
  table: ({ children }: any) => <table className="border-collapse border border-slate-600 mb-4">{children}</table>,
  th: ({ children }: any) => <th className="border border-slate-600 px-3 py-2 bg-slate-800 text-white font-semibold">{children}</th>,
  td: ({ children }: any) => <td className="border border-slate-600 px-3 py-2 text-white">{children}</td>,
};

  return (
    <div 
      ref={contentRef}
      className={`relative ${className}`}
    >
      {enableMarkdown ? (
        <div className="leading-relaxed">
          <ReactMarkdown 
            remarkPlugins={[remarkGfm]}
            components={markdownComponents}
          >
            {displayedContent}
          </ReactMarkdown>
          {isStreaming && displayedContent.length < content.length && (
            <span className="inline-flex ml-1 opacity-60">
              <span className="w-0.5 h-5 bg-blue-400 animate-pulse"></span>
            </span>
          )}
        </div>
      ) : (
        <div className="whitespace-pre-wrap leading-relaxed">
          {displayedContent}
          {isStreaming && displayedContent.length < content.length && (
            <span className="inline-flex ml-1 opacity-60">
              <span className="w-0.5 h-5 bg-blue-400 animate-pulse"></span>
            </span>
          )}
        </div>
      )}
    </div>
  );
}

interface TypewriterTextProps {
  text: string;
  speed?: number;
  className?: string;
  onComplete?: () => void;
}

export function TypewriterText({ 
  text, 
  speed = 30, 
  className = '', 
  onComplete 
}: TypewriterTextProps) {
  const [displayedText, setDisplayedText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (currentIndex < text.length) {
      const timer = setTimeout(() => {
        setDisplayedText(prev => prev + text[currentIndex]);
        setCurrentIndex(prev => prev + 1);
      }, speed);

      return () => clearTimeout(timer);
    } else if (onComplete) {
      onComplete();
    }
  }, [currentIndex, text, speed, onComplete]);

  // Reset when text changes
  useEffect(() => {
    setDisplayedText('');
    setCurrentIndex(0);
  }, [text]);

  return (
    <span className={className}>
      {displayedText}
      {currentIndex < text.length && (
        <span className="animate-pulse text-blue-400 ml-1">|</span>
      )}
    </span>
  );
} 